<script setup lang="ts">
import type { ContentProduct } from '~/models/Content/ContentProduct'
import type { ContentPackage } from '~/models/Content/ContentPackage'
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import useAccordion from '~/composables/useAccordion'
import useContentApi from '~/api/contentApi'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import HeaderPreviewAccordion from '~/components/subject/HeaderPreviewAccordion.vue'
import ProductGenericCard from '~/components/cards/ProductGenericCard.vue'

const {
  product,
  section,
  hash,
  index,
  isCollapsible,
  theme
} = defineProps<{
  product: ContentProduct
  section: ContentPackage
  index: number
  hash: string
  isCollapsible: boolean
  theme: Record<number, string>
}>()

const { findContents } = useContentApi()
const { addAccordion } = useAccordion()

const children = computed(() => data?.value ?? [])

const { data, isLoading } = useQuery({
  enabled: computed(() => !!section.mainLocationId),
  queryKey: ['header-children', section.mainLocationId],
  queryFn: () => findContents({
    parentLocationIdCriterion: [section.mainLocationId],
    sortField: section.sortField,
    sortOrder: section.sortOrder,
  }),
  staleTime: Infinity,
})
</script>

<template>
  <section :id="`${index + 1}`" class="relative">
    <AuSkeletonWrapper
      v-if="isLoading"
      class="max-w-screen-au mx-auto space-y-4 px-4 py-8 sm:px-8"
    >
      <AuSkeleton
        height="36px"
        width="120px"
        :background="theme[50]"
      />
      <ul class="xs:grid-cols-2 grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-4">
        <AuSkeleton
          v-for="i in 4"
          :key="i"
          height="280px"
          :background="theme[50]"
        />
      </ul>
    </AuSkeletonWrapper>
    <HeaderPreviewAccordion
      v-else-if="isCollapsible"
      :id="`${index + 1}`"
      :header="product"
      :section="section"
      :open="`#${index + 1}` === hash"
      title-style="text-xl font-semibold uppercase"
    >
      <ul class="xs:grid-cols-2 grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-4">
        <li
          v-for="(childContent, childIndex) in children"
          :key="childContent.locationId"
        >
          <ProductGenericCard
            :resource="childContent"
            :loading="childIndex === 0 ? 'eager' : 'lazy'"
            @click="addAccordion(product.locationId, section.locationId)"
          />
        </li>
      </ul>
    </HeaderPreviewAccordion>
    <div
      v-else
      class="max-w-screen-au mx-auto space-y-4 px-4 py-12 sm:px-8"
    >
      <span class="flex flex-col space-y-4">
        <h2
          class="text-xl font-semibold uppercase"
          v-text="section.title"
        />
        <div v-if="section.intro" class="max-w-prose text-xl font-semibold">
          <RichTextRenderer :text="section.intro" />
        </div>
      </span>
      <ul class="xs:grid-cols-2 grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-4">
        <li
          v-for="(childContent, childIndex) in children"
          :key="childContent.locationId"
        >
          <ProductGenericCard
            :resource="childContent"
            :loading="childIndex === 0 ? 'eager' : 'lazy'"
          />
        </li>
      </ul>
    </div>
  </section>
</template>
